/* ============================================================
   NORTHEN TRADING LABS — Market Status Page
   Polling, rendering, filtering, accordion
   ============================================================ */

document.addEventListener('DOMContentLoaded', function () {

  // Nav / theme toggle are owned by the shared navbar partial (_NavPublic).

  // ============================================================
  // SCROLL REVEAL
  // ============================================================
  var revealEls = document.querySelectorAll('.reveal-up, .reveal-left');
  var revealObs = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        var el = entry.target;
        var delay = parseInt(el.dataset.delay || '0');
        setTimeout(function () { el.classList.add('visible'); }, delay);
        revealObs.unobserve(el);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
  revealEls.forEach(function (el) { revealObs.observe(el); });

  // ============================================================
  // STATE
  // ============================================================
  var mktGrid = document.getElementById('mkt-grid');
  var mktLoading = document.getElementById('mkt-loading');
  var filtersEl = document.getElementById('mkt-filters');
  var strengthSlider = document.getElementById('mkt-strength-slider');
  var strengthValue = document.getElementById('mkt-strength-value');

  var IDLE_INTERVAL = 300000;
  var MAX_BACKOFF = 300000;
  var BUFFER_MS = 15000;
  // Up to 10 s more, picked once per page. Without it every open Market Status page in the world
  // refreshed at the same wall-clock second (bar close + 15 s) and the server built all of their
  // responses at once. Fixed per page so the countdown counts to a steady time.
  var JITTER_MS = Math.floor(Math.random() * 10000);
  var TF_MINUTES = { M1: 1, M5: 5, M15: 15, M30: 30, H1: 60, H4: 240, D1: 1440, W1: 10080, MN: 43200 };

  var timerId = null;
  var countdownId = null;
  var consecutiveErr = 0;
  var lastData = null;
  var hasRecentData = true;
  var currentFilter = 'all';
  var expandedSymbol = null;
  var lastCategories = null;
  var smallestIntervalMin = 5;
  var nextRefreshTime = null;
  var lastUpdatedUtc = null;

  var countdownLabel = document.getElementById('mkt-countdown-label');

  var activeRegimes = new Set(['bullish', 'bearish', 'ranging', 'transitioning', 'transitioning-bullish', 'transitioning-bearish', 'contested']);
  var minStrength = 0;

  // ============================================================
  // HELPERS
  // ============================================================
  function esc(s) {
    var el = document.createElement('span');
    el.textContent = s == null ? '' : String(s);
    return el.innerHTML;
  }

  // ============================================================
  // REGIME HELPERS
  // ============================================================
  var BULLISH_SIDE = new Set([1, 3, 5, 7, 8, 9, 10, 11, 17, 19, 21]);

  function regimePipClass(rc) {
    if (rc === 'bullish' || rc === 'transitioning-bullish') return 'mkt-pip--bullish';
    if (rc === 'bearish' || rc === 'transitioning-bearish') return 'mkt-pip--bearish';
    if (rc === 'ranging') return 'mkt-pip--ranging';
    if (rc === 'transitioning') return 'mkt-pip--transitioning';
    return 'mkt-pip--ranging';
  }

  function strengthPercent(s) {
    return Math.min(100, Math.round((s / 1023) * 100));
  }

  // ============================================================
  // CONFLUENCE DETECTION
  // ============================================================
  function getConfluence(pair) {
    if (!pair.timeframes) return null;
    var bull = 0, bear = 0;
    Object.values(pair.timeframes).forEach(function (tf) {
      if (tf.regimeClass === 'bullish') bull++;
      else if (tf.regimeClass === 'bearish') bear++;
    });
    if (bull >= 3) return 'confluence-bullish';
    if (bear >= 3) return 'confluence-bearish';
    return null;
  }

  // ============================================================
  // CLOCK-ALIGNED REFRESH
  // ============================================================
  function getSmallestInterval(timeframes) {
    var min = Infinity;
    timeframes.forEach(function (tf) {
      var mins = TF_MINUTES[tf.toUpperCase()];
      if (mins && mins < min) min = mins;
    });
    return min === Infinity ? 5 : min;
  }

  function calcNextRefresh(intervalMinutes) {
    var now = new Date();
    var intervalMs = intervalMinutes * 60 * 1000;
    var midnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    var msSinceMidnight = now.getTime() - midnight.getTime();
    var nextSlot = Math.floor(msSinceMidnight / intervalMs) + 1;
    return new Date(midnight.getTime() + nextSlot * intervalMs + BUFFER_MS + JITTER_MS);
  }

  function updateCountdown() {
    if (!nextRefreshTime || !countdownLabel) return;
    var remaining = nextRefreshTime.getTime() - Date.now();
    if (remaining <= 0) {
      countdownLabel.textContent = 'Refreshing…';
      return;
    }
    var totalSec = Math.ceil(remaining / 1000);
    var min = Math.floor(totalSec / 60);
    var sec = totalSec % 60;
    countdownLabel.textContent = min > 0
      ? 'Next refresh ' + min + 'm ' + (sec < 10 ? '0' : '') + sec + 's'
      : 'Next refresh ' + sec + 's';
  }

  // ============================================================
  // CATEGORY FILTER BUTTONS
  // ============================================================
  function buildFilterButtons(categories) {
    var key = JSON.stringify(categories);
    if (key === lastCategories) return;
    lastCategories = key;

    filtersEl.innerHTML = '<button class="mkt-filter active" data-mkt-filter="all">All</button>';
    categories.forEach(function (cat) {
      var btn = document.createElement('button');
      btn.className = 'mkt-filter';
      btn.dataset.mktFilter = cat;
      btn.textContent = cat;
      filtersEl.appendChild(btn);
    });

    filtersEl.querySelectorAll('.mkt-filter').forEach(function (btn) {
      btn.addEventListener('click', function () {
        filtersEl.querySelectorAll('.mkt-filter').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        currentFilter = btn.dataset.mktFilter;
        applyFilters();
      });
    });

    var active = filtersEl.querySelector('[data-mkt-filter="' + CSS.escape(currentFilter) + '"]');
    if (active) {
      filtersEl.querySelectorAll('.mkt-filter').forEach(function (b) { b.classList.remove('active'); });
      active.classList.add('active');
    }
  }

  // ============================================================
  // REGIME TOGGLES
  // ============================================================
  document.querySelectorAll('.mkt-toggle').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var regime = btn.dataset.regime;
      var siblings = regime === 'transitioning' ? ['transitioning-bullish', 'transitioning-bearish'] : [];
      if (activeRegimes.has(regime)) {
        activeRegimes.delete(regime);
        siblings.forEach(function (s) { activeRegimes.delete(s); });
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      } else {
        activeRegimes.add(regime);
        siblings.forEach(function (s) { activeRegimes.add(s); });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
      }
      applyFilters();
    });
  });

  // ============================================================
  // STRENGTH SLIDER
  // ============================================================
  if (strengthSlider) {
    strengthSlider.addEventListener('input', function () {
      minStrength = parseInt(strengthSlider.value);
      if (strengthValue) strengthValue.textContent = minStrength + '%';
      applyFilters();
    });
  }

  // ============================================================
  // BUILD MATRIX
  // ============================================================
  function buildMatrix(data) {
    var existing = mktGrid.querySelector('.mkt-matrix');
    if (existing) existing.remove();
    var existingCards = mktGrid.querySelector('.mkt-cards');
    if (existingCards) existingCards.remove();
    if (mktLoading) mktLoading.remove();

    if (data.categories) buildFilterButtons(data.categories);

    if (!data.pairs || data.pairs.length === 0) {
      var empty = document.createElement('div');
      empty.className = 'mkt-empty';
      empty.innerHTML = '<div class="mkt-empty-title">No market data available</div>' +
        '<p>Data will appear here once the SMC indicator begins reporting regime classifications.</p>';
      mktGrid.appendChild(empty);
      return;
    }

    var errEl = mktGrid.querySelector('.mkt-empty, .mkt-error');
    if (errEl) errEl.remove();

    var timeframes = data.timeframes || [];
    var gridCols = '140px repeat(' + timeframes.length + ', minmax(0, 1fr))';

    var matrix = document.createElement('div');
    matrix.className = 'mkt-matrix';

    // Header
    matrix.style.gridTemplateColumns = gridCols;

    var header = document.createElement('div');
    header.className = 'mkt-matrix-header';
    var headerHtml = '<div class="mkt-pair-head">PAIR</div>';
    timeframes.forEach(function (tf) {
      headerHtml += '<div class="mkt-tf-head">' + esc(tf) + '</div>';
    });
    header.innerHTML = headerHtml;
    matrix.appendChild(header);

    // Body
    var body = document.createElement('div');
    body.className = 'mkt-matrix-body';

    data.pairs.forEach(function (pair) {
      var confluence = getConfluence(pair);

      var rowRegimes = new Set();
      var rowMaxStrength = 0;
      if (pair.timeframes) {
        Object.values(pair.timeframes).forEach(function (tf) {
          if (tf.regimeClass) rowRegimes.add(tf.regimeClass);
          if (tf.isContested) rowRegimes.add('contested');
          var pct = strengthPercent(tf.strength);
          if (pct > rowMaxStrength) rowMaxStrength = pct;
        });
      }

      // Main row
      var row = document.createElement('div');
      row.className = 'mkt-row';
      row.setAttribute('tabindex', '0');
      row.setAttribute('role', 'button');
      row.setAttribute('aria-expanded', 'false');
      if (confluence) row.classList.add(confluence);
      row.dataset.category = pair.category;
      row.dataset.symbol = pair.symbol;
      row.dataset.regimes = Array.from(rowRegimes).join(',');
      row.dataset.maxStrength = rowMaxStrength;

      // Pair column
      var pairCol = document.createElement('div');
      pairCol.className = 'mkt-pair-col';
      pairCol.innerHTML =
        '<div class="mkt-pair-inner">' +
          '<span class="mkt-symbol">' + esc(pair.symbol) + '</span>' +
          '<span class="mkt-expand-icon"></span>' +
        '</div>' +
        '<span class="mkt-cat-label">' + esc((pair.category || '').toUpperCase()) + '</span>';
      row.appendChild(pairCol);

      // Timeframe cells
      timeframes.forEach(function (tf) {
        var tfData = pair.timeframes ? pair.timeframes[tf] : null;
        row.appendChild(buildCell(tfData));
      });

      body.appendChild(row);

      // Detail panel
      var detail = document.createElement('div');
      detail.className = 'mkt-detail';
      detail.dataset.symbol = pair.symbol;
      buildDetailContent(detail, pair, timeframes);
      body.appendChild(detail);

      // Click to expand
      row.addEventListener('click', function () { toggleDetail(row, detail, pair.symbol); });
      row.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleDetail(row, detail, pair.symbol);
        }
      });
    });

    matrix.appendChild(body);
    mktGrid.appendChild(matrix);
    mktGrid.appendChild(buildCardView(data, timeframes));

    applyFilters();

    // Re-expand previously expanded symbol
    if (expandedSymbol) {
      var sym = expandedSymbol;
      var reRow = findBySymbol(body, '.mkt-row', sym);
      var reDet = findBySymbol(body, '.mkt-detail', sym);
      if (reRow && reDet) {
        reRow.classList.add('expanded');
        reRow.setAttribute('aria-expanded', 'true');
        reDet.style.maxHeight = reDet.scrollHeight + 'px';
        reDet.classList.add('open');
      }
      var card = findBySymbol(mktGrid.querySelector('.mkt-cards'), '.mkt-card', sym);
      if (card) {
        var cdet = card.querySelector('.mkt-card-detail');
        if (cdet) {
          card.classList.add('expanded');
          cdet.style.maxHeight = cdet.scrollHeight + 'px';
          cdet.classList.add('open');
        }
      }
    }
  }

  function findBySymbol(parent, selector, symbol) {
    if (!parent) return null;
    var els = parent.querySelectorAll(selector);
    for (var i = 0; i < els.length; i++) {
      if (els[i].dataset.symbol === symbol) return els[i];
    }
    return null;
  }

  function buildCell(tfData) {
    var cell = document.createElement('div');

    if (!tfData) {
      cell.className = 'mkt-cell mkt-cell--empty';
      cell.innerHTML =
        '<div class="mkt-cell-top">' +
          '<div class="mkt-cell-regime">' +
            '<span class="mkt-cell-label">&mdash;</span>' +
          '</div>' +
        '</div>' +
        '<div class="mkt-cell-bar"><div class="mkt-cell-fill mkt-fill--empty" style="width:0%"></div></div>';
      return cell;
    }

    var rc = tfData.regimeClass || 'ranging';
    var pct = strengthPercent(tfData.strength);
    var pip = regimePipClass(rc);
    var contested = tfData.isContested ? ' mkt-cell--contested' : '';

    cell.className = 'mkt-cell mkt-cell--' + rc + contested;
    cell.innerHTML =
      '<div class="mkt-cell-top">' +
        '<div class="mkt-cell-regime">' +
          '<span class="mkt-regime-pip ' + pip + '"></span>' +
          '<span class="mkt-cell-label">' + esc(tfData.label || rc) + '</span>' +
        '</div>' +
        '<span class="mkt-cell-pct">' + pct + '%</span>' +
      '</div>' +
      '<div class="mkt-cell-bar"><div class="mkt-cell-fill mkt-fill--' + rc + '" style="width:0%"></div></div>';

    requestAnimationFrame(function () { requestAnimationFrame(function () {
      var fill = cell.querySelector('.mkt-cell-fill');
      if (fill) fill.style.width = pct + '%';
    }); });

    return cell;
  }

  function buildCardView(data, timeframes) {
    var cards = document.createElement('div');
    cards.className = 'mkt-cards';

    data.pairs.forEach(function (pair) {
      var confluence = getConfluence(pair);
      var cardRegimes = new Set();
      var cardMaxStrength = 0;
      if (pair.timeframes) {
        Object.values(pair.timeframes).forEach(function (tf) {
          if (tf.regimeClass) cardRegimes.add(tf.regimeClass);
          if (tf.isContested) cardRegimes.add('contested');
          var p = strengthPercent(tf.strength);
          if (p > cardMaxStrength) cardMaxStrength = p;
        });
      }

      var card = document.createElement('div');
      card.className = 'mkt-card';
      if (confluence) card.classList.add(confluence);
      card.dataset.category = pair.category;
      card.dataset.symbol = pair.symbol;
      card.dataset.regimes = Array.from(cardRegimes).join(',');
      card.dataset.maxStrength = cardMaxStrength;

      var hdr = document.createElement('div');
      hdr.className = 'mkt-card-header';
      hdr.innerHTML =
        '<div>' +
          '<div class="mkt-card-symbol">' + esc(pair.symbol) + '</div>' +
          '<div class="mkt-cat-label">' + esc((pair.category || '').toUpperCase()) + '</div>' +
        '</div>' +
        '<span class="mkt-expand-icon"></span>';
      card.appendChild(hdr);

      var cardBody = document.createElement('div');
      cardBody.className = 'mkt-card-body';

      timeframes.forEach(function (tf) {
        var tfData = pair.timeframes ? pair.timeframes[tf] : null;
        var tfRow = document.createElement('div');
        tfRow.className = 'mkt-card-tf';

        if (!tfData) {
          tfRow.classList.add('mkt-card-tf--empty');
          tfRow.innerHTML =
            '<span class="mkt-card-tf-name">' + esc(tf) + '</span>' +
            '<span class="mkt-regime-pip mkt-pip--ranging"></span>' +
            '<span class="mkt-card-tf-label mkt-card-tf-label--empty">&mdash;</span>';
        } else {
          var rc = tfData.regimeClass || 'ranging';
          var pct = strengthPercent(tfData.strength);
          tfRow.innerHTML =
            '<span class="mkt-card-tf-name">' + esc(tf) + '</span>' +
            '<span class="mkt-regime-pip ' + regimePipClass(rc) + '"></span>' +
            '<span class="mkt-card-tf-label mkt-card-tf-label--' + rc + '">' + esc(tfData.label || rc) + '</span>' +
            '<div class="mkt-card-tf-bar"><div class="mkt-card-tf-fill mkt-fill--' + rc + '" style="width:0%"></div></div>' +
            '<span class="mkt-card-tf-pct">' + pct + '%</span>';

          requestAnimationFrame(function () { requestAnimationFrame(function () {
            var fill = tfRow.querySelector('.mkt-card-tf-fill');
            if (fill) fill.style.width = pct + '%';
          }); });
        }

        cardBody.appendChild(tfRow);
      });

      card.appendChild(cardBody);

      var detail = document.createElement('div');
      detail.className = 'mkt-card-detail';
      detail.dataset.symbol = pair.symbol;
      buildDetailContent(detail, pair, timeframes);
      card.appendChild(detail);

      hdr.addEventListener('click', function () {
        var wasExpanded = card.classList.contains('expanded');
        cards.querySelectorAll('.mkt-card.expanded').forEach(function (c) {
          c.classList.remove('expanded');
          var d = c.querySelector('.mkt-card-detail');
          if (d) { d.style.maxHeight = null; d.classList.remove('open'); }
        });
        if (!wasExpanded) {
          card.classList.add('expanded');
          detail.style.maxHeight = detail.scrollHeight + 'px';
          detail.classList.add('open');
          expandedSymbol = pair.symbol;
        } else {
          expandedSymbol = null;
        }
      });

      cards.appendChild(card);
    });

    return cards;
  }

  // ============================================================
  // DETAIL PANEL
  // ============================================================
  function buildDetailContent(container, pair, timeframes) {
    if (!pair.timeframes) {
      var emptyDiv = document.createElement('div');
      emptyDiv.className = 'mkt-detail-inner';
      var emptySpan = document.createElement('span');
      emptySpan.className = 'mkt-detail-empty';
      emptySpan.textContent = 'No detail available.';
      emptyDiv.appendChild(emptySpan);
      container.appendChild(emptyDiv);
      return;
    }

    var activeTfs = timeframes.filter(function (tf) { return pair.timeframes[tf]; });
    if (activeTfs.length === 0) {
      var emptyDiv2 = document.createElement('div');
      emptyDiv2.className = 'mkt-detail-inner';
      var emptySpan2 = document.createElement('span');
      emptySpan2.className = 'mkt-detail-empty';
      emptySpan2.textContent = 'No detail available.';
      emptyDiv2.appendChild(emptySpan2);
      container.appendChild(emptyDiv2);
      return;
    }

    var grid = document.createElement('div');
    grid.className = 'mkt-detail-inner mkt-detail-grid';

    activeTfs.forEach(function (tfName) {
      var tf = pair.timeframes[tfName];
      var conviction = tf.convictionLevel || 'None';
      var stable = tf.convictionStable;
      var aligned = tf.directionAligned;
      var contested = tf.isContested;
      var fvgChip = tf.fvgChip || '';
      var bosChip = tf.bosChip || '';
      var updated = tf.updatedUtc ? timeAgo(new Date(tf.updatedUtc)) : 'Unknown';
      var rc = tf.regimeClass || 'ranging';

      var tfDiv = document.createElement('div');
      tfDiv.className = 'mkt-detail-tf';

      var headHtml =
        '<div class="mkt-detail-tf-head">' +
          '<span class="mkt-regime-pip ' + regimePipClass(rc) + '"></span>' +
          '<span class="mkt-detail-tf-name">' + esc(tfName) + '</span>' +
        '</div>';

      var bodyHtml =
        '<div class="mkt-detail-tf-body">' +
          '<div class="mkt-detail-item">' +
            '<span class="mkt-detail-label">Conviction</span>' +
            '<span class="mkt-detail-value">' + esc(conviction) + '</span>' +
          '</div>' +
          '<div class="mkt-detail-item">' +
            '<span class="mkt-detail-label">Direction</span>' +
            '<span class="mkt-detail-value ' + (aligned ? 'aligned' : 'diverging') + '">' +
              (aligned ? 'Aligned' : 'Diverging') +
            '</span>' +
          '</div>' +
          '<div class="mkt-detail-item">' +
            '<span class="mkt-detail-label">Stability</span>' +
            '<span class="mkt-detail-value ' + (stable ? 'stable' : 'shifting') + '">' +
              (stable ? 'Stable' : 'Shifting') +
            '</span>' +
          '</div>' +
          (contested
            ? '<div class="mkt-detail-item">' +
                '<span class="mkt-detail-label">Status</span>' +
                '<span class="mkt-detail-value contested">Contested</span>' +
              '</div>'
            : '') +
          (fvgChip
            ? '<div class="mkt-detail-item">' +
                '<span class="mkt-detail-label">FVG</span>' +
                '<span class="mkt-detail-value mkt-detail-chip">' + esc(fvgChip) + '</span>' +
              '</div>'
            : '') +
          (bosChip
            ? '<div class="mkt-detail-item">' +
                '<span class="mkt-detail-label">BOS</span>' +
                '<span class="mkt-detail-value mkt-detail-chip">' + esc(bosChip) + '</span>' +
              '</div>'
            : '') +
          '<div class="mkt-detail-item">' +
            '<span class="mkt-detail-label">Updated</span>' +
            '<span class="mkt-detail-value">' + esc(updated) + '</span>' +
          '</div>' +
        '</div>';

      tfDiv.innerHTML = headHtml + bodyHtml;
      grid.appendChild(tfDiv);
    });

    container.appendChild(grid);
  }

  function toggleDetail(row, detail, symbol) {
    var isExpanded = row.classList.contains('expanded');

    // Close all
    mktGrid.querySelectorAll('.mkt-row.expanded').forEach(function (r) {
      r.classList.remove('expanded');
      r.setAttribute('aria-expanded', 'false');
    });
    mktGrid.querySelectorAll('.mkt-detail.open').forEach(function (d) {
      d.style.maxHeight = null;
      d.classList.remove('open');
    });

    if (!isExpanded) {
      row.classList.add('expanded');
      row.setAttribute('aria-expanded', 'true');
      detail.style.maxHeight = detail.scrollHeight + 'px';
      detail.classList.add('open');
      expandedSymbol = symbol;
    } else {
      expandedSymbol = null;
    }
  }

  // ============================================================
  // FILTERING (all three dimensions)
  // ============================================================
  function applyFilters() {
    mktGrid.querySelectorAll('.mkt-row').forEach(function (row) {
      var catMatch = currentFilter === 'all' || row.dataset.category === currentFilter;

      var rowRegimes = row.dataset.regimes ? row.dataset.regimes.split(',') : [];
      var regimeMatch = rowRegimes.some(function (r) { return activeRegimes.has(r); });

      var maxStr = parseInt(row.dataset.maxStrength || '0');
      var strengthMatch = maxStr >= minStrength;

      var show = catMatch && regimeMatch && strengthMatch;
      row.classList.toggle('hidden', !show);
    });

    mktGrid.querySelectorAll('.mkt-detail').forEach(function (det) {
      var sym = det.dataset.symbol;
      var row = findBySymbol(mktGrid, '.mkt-row', sym);
      if (row) det.style.display = row.classList.contains('hidden') ? 'none' : '';
    });

    mktGrid.querySelectorAll('.mkt-card').forEach(function (card) {
      var catMatch = currentFilter === 'all' || card.dataset.category === currentFilter;
      var cardRegimes = card.dataset.regimes ? card.dataset.regimes.split(',') : [];
      var regimeMatch = cardRegimes.some(function (r) { return activeRegimes.has(r); });
      var maxStr = parseInt(card.dataset.maxStrength || '0');
      var strengthMatch = maxStr >= minStrength;
      card.classList.toggle('hidden', !(catMatch && regimeMatch && strengthMatch));
    });
  }

  // ============================================================
  // TIMESTAMP
  // ============================================================
  function timeAgo(date) {
    var seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 0) return 'Just now';
    if (seconds < 10) return 'Just now';
    if (seconds < 60) return seconds + 's ago';
    if (seconds < 3600) return Math.floor(seconds / 60) + 'm ago';
    if (seconds < 86400) return Math.floor(seconds / 3600) + 'h ago';
    return Math.floor(seconds / 86400) + 'd ago';
  }

  // ============================================================
  // DATA FETCHING & POLLING
  // ============================================================
  var regimeApiUrl = (mktGrid && mktGrid.dataset.apiUrl) || '/MarketStatus/Regimes';

  async function fetchRegimes() {
    try {
      var resp = await fetch(regimeApiUrl, { credentials: 'same-origin' });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      var data = await resp.json();
      consecutiveErr = 0;
      hasRecentData = data.hasRecentData !== false;

      var serverUpdated = data.lastUpdatedUtc || null;
      if (lastData && lastUpdatedUtc && serverUpdated === lastUpdatedUtc) {
        return null;
      }
      lastUpdatedUtc = serverUpdated;

      lastData = data;
      window.marketStatusData = data;
      return data;
    } catch (e) {
      consecutiveErr++;
      if (typeof console !== 'undefined') console.warn('MarketStatus fetch failed:', e.message || e);
      return null;
    }
  }

  async function poll() {
    if (document.hidden) {
      scheduleNextPoll();
      return;
    }
    var data = await fetchRegimes();
    if (data) {
      buildMatrix(data);
      if (data.timeframes && data.timeframes.length > 0) {
        smallestIntervalMin = getSmallestInterval(data.timeframes);
      }
    } else if (!lastData) {
      if (mktLoading) mktLoading.remove();
      var existing = mktGrid.querySelector('.mkt-error');
      if (!existing) {
        var errEl = document.createElement('div');
        errEl.className = 'mkt-error';
        errEl.innerHTML = '<div class="mkt-error-title">Unable to load data</div>' +
          '<p>Regime data is temporarily unavailable. Retrying automatically.</p>';
        mktGrid.appendChild(errEl);
      }
    }
    scheduleNextPoll();
  }

  function scheduleNextPoll() {
    clearTimeout(timerId);

    if (consecutiveErr > 0) {
      var backoff = Math.min(MAX_BACKOFF, 60000 * Math.pow(2, consecutiveErr - 1));
      nextRefreshTime = new Date(Date.now() + backoff);
      timerId = setTimeout(poll, backoff);
      return;
    }

    if (!hasRecentData) {
      nextRefreshTime = new Date(Date.now() + IDLE_INTERVAL);
      timerId = setTimeout(poll, IDLE_INTERVAL);
      return;
    }

    nextRefreshTime = calcNextRefresh(smallestIntervalMin);
    var delay = Math.max(1000, nextRefreshTime.getTime() - Date.now());
    timerId = setTimeout(poll, delay);
  }

  function startCountdown() {
    if (countdownId) return;
    countdownId = setInterval(updateCountdown, 1000);
  }

  function stopCountdown() {
    if (countdownId) {
      clearInterval(countdownId);
      countdownId = null;
    }
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      stopCountdown();
    } else {
      startCountdown();
      clearTimeout(timerId);
      if (nextRefreshTime && Date.now() >= nextRefreshTime.getTime()) {
        poll();
      } else {
        scheduleNextPoll();
      }
    }
  });

  // Start
  poll();
  startCountdown();


});
